import { useEffect, useState } from 'react';
import {
  FiInfo, FiBarChart2, FiCheckCircle, FiClock,
  FiAlertCircle, FiInbox, FiDownload, FiHelpCircle,
} from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import { esicPortalService, type IndicadoresEsic, TIPO_PEDIDO_LABEL, type TipoPedidoEsic } from '../../services/esic.service';

const TIPO_COR: Record<TipoPedidoEsic, string> = {
  informacao: '#3b82f6',
  documento: '#8b5cf6',
  dado: '#10b981',
  outro: '#64748b',
};

export function EsicTransparenciaPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [dados, setDados] = useState<IndicadoresEsic | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [faq, setFaq] = useState<Array<{ id: string; pergunta: string; resposta: string }>>([]);
  const [faqAberto, setFaqAberto] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('faq_sic')
      .select('id, pergunta, resposta')
      .eq('estabelecimento_id', getEstabelecimentoAtualId())
      .eq('ativo', true)
      .order('ordem')
      .then(({ data }) => setFaq((data ?? []) as Array<{ id: string; pergunta: string; resposta: string }>));
  }, []);

  useEffect(() => {
    setCarregando(true);
    const id = getEstabelecimentoAtualId();
    Promise.all([
      esicPortalService.indicadores(id, ano),
      estab ? Promise.resolve({ data: estab, error: null }) : supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', id).maybeSingle(),
    ])
      .then(([d, e]) => {
        setDados(d);
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  function exportarCSV() {
    if (!dados) return;
    const linhas = [
      ['Indicador', 'Valor'],
      ['Ano de referência', dados.ano.toString()],
      ['Total de pedidos originais', dados.total_pedidos.toString()],
      ['Deferidos', dados.deferidos.toString()],
      ['Indeferidos', dados.indeferidos.toString()],
      ['Respondidos no prazo', dados.respondidos_no_prazo.toString()],
      ['Respondidos fora do prazo', dados.respondidos_fora_do_prazo.toString()],
      ['Pedidos com prazo prorrogado', dados.prorrogados.toString()],
      ['Tempo médio de resposta (horas)', dados.tempo_medio_resposta_horas.toString()],
      ['Recursos 1ª instância', dados.recursos.instancia_1.toString()],
      ['Recursos 2ª instância', dados.recursos.instancia_2.toString()],
      ['Recursos 3ª instância', dados.recursos.instancia_3.toString()],
      ['Recursos 4ª instância', dados.recursos.instancia_4.toString()],
    ];
    Object.entries(dados.distribuicao_por_tipo).forEach(([tipo, qtd]) => {
      linhas.push([`Distribuição: ${tipo}`, qtd.toString()]);
    });
    const csv = linhas.map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esic_${dados.ano}_${(estab?.nome ?? 'prefeitura').toLowerCase().replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportarJSON() {
    if (!dados) return;
    const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esic_${dados.ano}_${(estab?.nome ?? 'prefeitura').toLowerCase().replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];

  const distribuicao = dados ? Object.entries(dados.distribuicao_por_tipo).sort((a, b) => b[1] - a[1]) : [];
  const totalDistribuicao = distribuicao.reduce((s, [, q]) => s + q, 0);
  const taxaDeferimento = dados && dados.total_pedidos > 0
    ? Math.round((dados.deferidos / dados.total_pedidos) * 100)
    : 0;
  const taxaPrazo = dados && (dados.respondidos_no_prazo + dados.respondidos_fora_do_prazo) > 0
    ? Math.round((dados.respondidos_no_prazo / (dados.respondidos_no_prazo + dados.respondidos_fora_do_prazo)) * 100)
    : 0;

  return (
    <PortalPage
      titulo="Transparência do e-SIC"
      subtitulo={`${estab?.nome ? estab.nome + ' · ' : ''}Relatório anual de pedidos de acesso à informação (Lei 12.527/2011)`}
      icone={<FiInfo size={26} />}
      voltarHref="/"
      largura="larga"
      acoes={
        <select
          value={ano}
          onChange={(e) => setAno(parseInt(e.target.value, 10))}
          className="h-9 rounded-md border border-white/20 bg-white/10 px-3 text-sm font-medium text-white focus:outline-none [&>option]:text-slate-900"
        >
          {opcoesAno.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      }
    >
      {dados && dados.total_pedidos > 0 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={exportarCSV}>
            <FiDownload size={14} /> CSV
          </Button>
          <Button variant="outline" onClick={exportarJSON}>
            <FiDownload size={14} /> JSON
          </Button>
        </div>
      )}

      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro ? (
        <Alert variante="erro">{erro}</Alert>
      ) : !dados || dados.total_pedidos === 0 ? (
        <SectionCard className="mt-5">
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiInbox className="text-slate-400" size={26} />
            </div>
            <p className="text-sm font-medium text-slate-700">Nenhum pedido de acesso à informação registrado em {ano}.</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-5 mt-5">
          {/* KPIs principais */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI icone={<FiBarChart2 size={16} />} label="Pedidos no ano" valor={dados.total_pedidos.toString()} />
            <KPI icone={<FiCheckCircle size={16} />} label="Deferidos" valor={dados.deferidos.toString()} hint={`${taxaDeferimento}% do total`} />
            <KPI icone={<FiAlertCircle size={16} />} label="Indeferidos" valor={dados.indeferidos.toString()} />
            <KPI icone={<FiClock size={16} />} label="Tempo médio" valor={formatarHoras(dados.tempo_medio_resposta_horas)} hint="da abertura à resposta" />
            <KPI icone={<FiCheckCircle size={16} />} label="No prazo" valor={`${taxaPrazo}%`} hint={`${dados.respondidos_no_prazo}/${dados.respondidos_no_prazo + dados.respondidos_fora_do_prazo}`} />
            <KPI icone={<FiClock size={16} />} label="Prorrogados" valor={dados.prorrogados.toString()} hint="+10 dias úteis" />
          </div>

          {/* Recursos */}
          <SectionCard titulo="Recursos por instância" icone={<FiBarChart2 size={18} />}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { n: 1, label: '1ª instância', valor: dados.recursos.instancia_1, descricao: 'Autoridade superior' },
                { n: 2, label: '2ª instância', valor: dados.recursos.instancia_2, descricao: 'Autoridade máxima' },
                { n: 3, label: '3ª instância', valor: dados.recursos.instancia_3, descricao: 'Controladoria municipal' },
                { n: 4, label: '4ª instância', valor: dados.recursos.instancia_4, descricao: 'Última instância' },
              ].map((r) => (
                <div key={r.n} className="bg-slate-50 border border-slate-200 rounded p-3">
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">{r.valor}</div>
                  <div className="text-xs font-medium text-slate-700 mt-0.5">{r.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.descricao}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Distribuição por tipo */}
          {distribuicao.length > 0 && (
            <SectionCard titulo="Pedidos por tipo" icone={<FiBarChart2 size={18} />}>
              <div className="space-y-3">
                {distribuicao.map(([tipo, qtd]) => {
                  const pct = totalDistribuicao > 0 ? (qtd / totalDistribuicao) * 100 : 0;
                  const corBar = TIPO_COR[tipo as TipoPedidoEsic] ?? '#64748b';
                  const label = TIPO_PEDIDO_LABEL[tipo as TipoPedidoEsic] ?? tipo;
                  return (
                    <div key={tipo}>
                      <div className="flex items-baseline justify-between mb-1 text-sm">
                        <span className="font-medium text-slate-900">{label}</span>
                        <span className="text-slate-600 tabular-nums">
                          {qtd} <span className="text-slate-400">({pct.toFixed(1)}%)</span>
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: corBar }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          <Alert variante="info">
            <strong>Lei 12.527/2011 (LAI)</strong> — A Lei de Acesso à Informação obriga a divulgação destes
            indicadores. A CGU divulga ranking nacional de transparência baseado nestes dados. Os dados
            são atualizados em tempo real conforme os pedidos são processados.
            <FiAlertCircle className="inline ml-1" size={12} />
          </Alert>

          {faq.length > 0 && (
            <SectionCard titulo="Perguntas frequentes" icone={<FiHelpCircle size={18} />}>
              <div className="divide-y divide-slate-100">
                {faq.map((f) => (
                  <div key={f.id} className="py-2">
                    <button
                      onClick={() => setFaqAberto((a) => (a === f.id ? null : f.id))}
                      className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-slate-800"
                    >
                      <span>{f.pergunta}</span>
                      <span className="text-slate-400">{faqAberto === f.id ? '–' : '+'}</span>
                    </button>
                    {faqAberto === f.id && <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-600">{f.resposta}</p>}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </PortalPage>
  );
}

function KPI({ icone, label, valor, hint }: { icone: React.ReactNode; label: string; valor: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-2">
          <div className="text-slate-400 flex-shrink-0 mt-1">{icone}</div>
          <div className="min-w-0">
            <div className="text-2xl font-bold text-slate-900 tabular-nums">{valor}</div>
            <div className="text-xs text-slate-500 mt-0.5">{label}</div>
            {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatarHoras(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  const dias = h / 24;
  return `${dias.toFixed(1)} dias`;
}
